import React, { useEffect, useCallback, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useParams } from 'react-router-dom'

import useQueryParams from 'hooks/useQueryParams'

import VendorsList from 'ui/pages/vendors'
import Item from 'ui/modules/lists/checkboxList/item'

import { openAddVendorModal } from 'services/modal/actions'
import { showSnackbar } from 'services/snackbar/actions'

import { getUserIsAdmin } from '../services/auth/selectors'
import {
  getVendorsInfo,
  getSelectedIdsOnPage,
  getPaginationInfo,
  getIsVendorsFetching,
  getVendorsError,
  getSearchOptions,
  getSearchFilters,
} from 'services/vendors/selectors'
import {
  fetchVendors,
  selectVendor,
  selectAll,
  resetSelected,
  fetchSearchOptions,
  clearSearchOptions,
  setSearchFilters,
  fakeRemoveVendors,
} from 'services/vendors/actions'

import * as actionTypesVendors from 'services/vendors/action-types'
import { exportToCSV } from '../services/searches/actions'
import useTranslation from '../hooks/useTranslations'

const Vendors = () => {
  const t = useTranslation()
  const { id } = useParams()
  const dispatch = useDispatch()
  const vendors = useSelector(getVendorsInfo)
  const selectedIds = useSelector(getSelectedIdsOnPage)
  const paginationInfo = useSelector(getPaginationInfo)
  const isFetching = useSelector(getIsVendorsFetching)
  const error = useSelector(getVendorsError)
  const searchOptions = useSelector(getSearchOptions)
  const history = useHistory()
  const query = useQueryParams()
  const { keyword } = useSelector(getSearchFilters)
  const [searchConfigParams] = useState('search_network=1')
  const [filters, setFilters] = useState(() => {
    const queryHomologated = query.get('homologated')
    const homologatedFilter =
      queryHomologated == null
        ? {}
        : { homologated: queryHomologated === 'true' }
    return { ...homologatedFilter }
  })
  const [searchValue, setSearchValue] = useState('')

  useEffect(() => {
    return () => handleRemoveFilters()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const updateURLParams = useCallback(
    ({ filters }) => {
      const urlParams = []
      if (filters.hasOwnProperty('homologated')) {
        urlParams.push(`homologated=${filters.homologated}`)
      }

      history.push({ search: urlParams.join('&') })
    },
    [history],
  )

  useEffect(() => {
    dispatch(fetchVendors({ keyword, filters, page: 1 }))
    updateURLParams({ filters })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters])

  const handleVendorClick = useCallback(
    id => {
      dispatch(selectVendor(id))
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  const handleGoToPage = useCallback(
    page => {
      dispatch(fetchVendors({ page, keyword, filters }))
    },
    [dispatch, filters, keyword],
  )

  const selectedCount = useMemo(() => Object.keys(selectedIds).length, [
    selectedIds,
  ])

  const handleBulkSelect = () => {
    if (selectedCount) {
      dispatch(resetSelected())
      return
    }
    dispatch(selectAll())
  }

  const hasFilters = useMemo(() => Object.keys(filters).length || keyword, [
    filters,
    keyword,
  ])

  const handleAddVendor = useCallback(() => {
    dispatch(openAddVendorModal())
  }, [dispatch])

  const handleGetSearchOptions = keyword => {
    dispatch(fetchSearchOptions(keyword, filters))
  }

  const handleClearSearchOptions = useCallback(() => {
    dispatch(clearSearchOptions())
  }, [dispatch])

  const handleRemoveFilters = useCallback(() => {
    dispatch(setSearchFilters({ keyword: '' }))
    setSearchValue('')
    setFilters({})
    handleClearSearchOptions()
  }, [dispatch, handleClearSearchOptions])

  const errorType = useMemo(() => {
    if (isFetching) return { type: '' }
    if (!vendors.length && hasFilters)
      return { type: 'noResultsFilter', action: handleRemoveFilters }
    if (!vendors.length) return { type: 'noResults', action: handleAddVendor }
    return {}
  }, [
    handleAddVendor,
    handleRemoveFilters,
    hasFilters,
    isFetching,
    vendors.length,
  ])

  const handleSearch = keyword => {
    dispatch(setSearchFilters({ keyword }))

    dispatch(fetchVendors({ page: 1, filters }))
  }

  const handleRemoveVendors = id => {
    const idsToRemove = id ? [id] : Object.keys(selectedIds)
    const message =
      idsToRemove.length === 1
        ? 'remove:single-vendor'
        : 'remove:multiple-vendors'

    dispatch(fakeRemoveVendors(idsToRemove, filters))
    dispatch(
      showSnackbar({
        message,
        actionToDo: {
          type: actionTypesVendors.REMOVE,
          payload: { ids: idsToRemove, filters },
        },
        actionOnCancel: {
          type: actionTypesVendors.FETCH_VENDORS,
          payload: { keyword, filters },
        },
      }),
    )
  }

  const searchFilterOptions = useCallback(options => options, [])
  const userIsAdmin = useSelector(getUserIsAdmin)

  const handleCSVExport = useCallback(() => {
    dispatch(
      exportToCSV({
        id,
        keyword,
        filters,
        data: searchConfigParams,
        translator: t,
      }),
    )
  }, [dispatch, filters, id, keyword, searchConfigParams, t])

  return (
    <VendorsList
      isLoading={isFetching}
      totalVendors={paginationInfo.total}
      totalPages={paginationInfo.pages}
      totalOnPage={vendors.length}
      currentPage={paginationInfo.page}
      goToPage={handleGoToPage}
      handleBulkSelect={handleBulkSelect}
      selectedCount={selectedCount}
      errorType={errorType}
      handleAddVendor={handleAddVendor}
      searchOptions={searchOptions}
      filterOptions={searchFilterOptions}
      getSearchOptions={handleGetSearchOptions}
      clearSearchOptions={handleClearSearchOptions}
      onSearch={handleSearch}
      onRemoveVendors={handleRemoveVendors}
      filters={filters}
      setFilters={setFilters}
      handleRemoveFilters={handleRemoveFilters}
      genericError={error}
      searchValue={searchValue}
      setSearchValue={setSearchValue}
      canAdd={userIsAdmin}
      canDelete={true}
      onCSVExport={handleCSVExport}
    >
      {vendors.map(vendor => (
        <Item
          key={vendor.id}
          title={vendor.name}
          id={vendor.id}
          isHomologated={vendor.homologated}
          showHomologated={true}
          onSelect={handleVendorClick}
          isSelected={!!selectedIds[vendor.id]}
          onRemoveVendor={handleRemoveVendors}
          canDelete={true}
          showResend={false}
        />
      ))}
    </VendorsList>
  )
}

export default Vendors
